import type {
  AdminSessionItem,
  AdminSessionView,
  AuditLogItem,
  CreateStoryInput,
  PaginationView,
  StoryDetail,
  StoryListItem,
  StoryStatus,
} from "@actale/contracts";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "./api.js";
import { DirectorOperationsPage } from "./DirectorOperationsPage.js";
import { DirectorProviderConfigPage } from "./DirectorProviderConfigPage.js";
import { DoubaoTtsGenerationPage } from "./DoubaoTtsGenerationPage.js";
import { OmniHumanGenerationPage } from "./OmniHumanGenerationPage.js";
import { SeedreamImagePage } from "./SeedreamImagePage.js";
import { SeedanceVideoPage } from "./SeedanceVideoPage.js";
import { PlaytestWorkbench } from "./PlaytestWorkbench.js";
import { ResponsibilityGuidePage } from "./ResponsibilityGuidePage.js";
import { StoryStructureEditor } from "./StoryStructureEditor.js";
import { SimulationWorkbench } from "./SimulationWorkbench.js";
import { VersionPublishWorkbench } from "./VersionPublishWorkbench.js";
import { WebsiteWaitlistPage } from "./WebsiteWaitlistPage.js";
import { formatUpdatedAt, storyStatusLabel } from "./story-utils.js";

function Brand(): React.ReactNode {
  return (
    <div className="brand" aria-label="同幕 ACTALE">
      <span className="brand-mark" aria-hidden="true">
        <span />
      </span>
      <span>
        <strong>同幕</strong>
        <small>ACTALE</small>
      </span>
    </div>
  );
}

function ErrorNotice({ message }: { message: string }): React.ReactNode {
  return (
    <div className="error-notice" role="alert">
      <span aria-hidden="true">!</span>
      {message}
    </div>
  );
}

function LoginView({ onLogin }: { onLogin: (session: AdminSessionView) => void }): React.ReactNode {
  const [username, setUsername] = useState("root");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      onLogin(await api.login({ username, password }));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "无法连接管理 API");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="auth-page">
      <section className="auth-panel">
        <Brand />
        <div className="auth-copy">
          <p className="eyebrow">故事线工作台</p>
          <h1>让每一次演绎，<br />都有清晰的世界边界。</h1>
          <p>创建角色、场景与结局，在发布之前完成规则测试和发布前检查。</p>
        </div>
        <div className="auth-footer">内部制作系统 · 未授权访问将被记录</div>
      </section>
      <section className="login-panel">
        <form className="login-card" onSubmit={submit}>
          <div>
            <p className="eyebrow">ACTALE ADMIN</p>
            <h2>登录工作台</h2>
            <p className="muted">使用内部管理员账号继续</p>
          </div>
          {error ? <ErrorNotice message={error} /> : null}
          <label>
            <span>账号</span>
            <input
              name="username"
              autoComplete="username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              required
            />
          </label>
          <label>
            <span>密码</span>
            <input
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="输入临时或已修改密码"
              required
            />
          </label>
          <button className="primary-button" disabled={submitting}>
            {submitting ? "正在验证…" : "进入故事线工作台"}
          </button>
        </form>
      </section>
    </main>
  );
}

function ChangePasswordView({ onChanged }: { onChanged: () => void }): React.ReactNode {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setError("两次输入的新密码不一致");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await api.changePassword(currentPassword, newPassword);
      onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "修改密码失败");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="password-page">
      <div className="password-shell">
        <Brand />
        <form className="password-card" onSubmit={submit}>
          <span className="step-badge">首次登录</span>
          <h1>先设置一个安全密码</h1>
          <p className="muted">至少 12 位，并同时包含大小写字母和数字。修改后，其他会话会被撤销。</p>
          {error ? <ErrorNotice message={error} /> : null}
          <label>
            <span>当前临时密码</span>
            <input type="password" autoComplete="current-password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} required />
          </label>
          <label>
            <span>新密码</span>
            <input type="password" autoComplete="new-password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} minLength={12} required />
          </label>
          <label>
            <span>确认新密码</span>
            <input type="password" autoComplete="new-password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} minLength={12} required />
          </label>
          <button className="primary-button" disabled={submitting}>{submitting ? "正在保存…" : "保存并进入工作台"}</button>
        </form>
      </div>
    </main>
  );
}

function CreateStoryDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}): React.ReactNode {
  const [form, setForm] = useState<CreateStoryInput>({ title: "", slug: "", summary: "", targetDurationMinutes: 5, minTurns: 3, maxTurns: 3 });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.createStory(form);
      onCreated();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "创建故事线失败");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="dialog" role="dialog" aria-modal="true" aria-labelledby="create-story-title" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <div>
            <p className="eyebrow">NEW STORY LINE</p>
            <h2 id="create-story-title">创建故事线</h2>
          </div>
          <button className="icon-button" type="button" aria-label="关闭" onClick={onClose}>×</button>
        </header>
        <form onSubmit={submit}>
          {error ? <ErrorNotice message={error} /> : null}
          <label>
            <span>故事线名称</span>
            <input value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} placeholder="例如：雾中的灯塔" maxLength={120} autoFocus required />
          </label>
          <label>
            <span>英文标识</span>
            <input value={form.slug} onChange={(event) => setForm({ ...form, slug: event.target.value.toLowerCase() })} placeholder="mist-over-lighthouse" pattern="[a-z0-9]+(?:-[a-z0-9]+)*" required />
            <small>用于识别故事线，创建后应保持稳定。</small>
          </label>
          <label>
            <span>一句话简介</span>
            <textarea value={form.summary} onChange={(event) => setForm({ ...form, summary: event.target.value })} placeholder="概括玩家将进入的困境和体验" maxLength={500} rows={4} />
          </label>
          <div className="form-grid">
            <label><span>目标体验（分钟）</span><input type="number" min={1} max={240} value={form.targetDurationMinutes} onChange={(event) => setForm({ ...form, targetDurationMinutes: Number(event.target.value) })} /></label>
            <label><span>用户回合范围</span><span className="inline-number-fields"><input aria-label="最低用户回合" type="number" min={1} max={100} value={form.minTurns} onChange={(event) => setForm({ ...form, minTurns: Number(event.target.value) })} /><b>至</b><input aria-label="最多用户回合" type="number" min={form.minTurns} max={100} value={form.maxTurns} onChange={(event) => setForm({ ...form, maxTurns: Number(event.target.value) })} /></span></label>
          </div>
          <footer>
            <button className="secondary-button" type="button" onClick={onClose}>取消</button>
            <button className="primary-button compact" disabled={submitting}>{submitting ? "正在创建…" : "创建草稿"}</button>
          </footer>
        </form>
      </section>
    </div>
  );
}

function StoryTable({
  stories,
  onOpen,
}: {
  stories: StoryListItem[];
  onOpen: (id: string) => void;
}): React.ReactNode {
  return (
    <div className="table-shell">
      <table className="story-table">
        <thead>
          <tr>
            <th>故事线</th>
            <th>状态</th>
            <th>内容结构</th>
            <th>草稿修订</th>
            <th>最近更新</th>
            <th><span className="sr-only">操作</span></th>
          </tr>
        </thead>
        <tbody>
          {stories.map((story) => (
            <tr key={story.id}>
              <td>
                <div className="story-title-cell">
                  <span className="story-glyph" aria-hidden="true">幕</span>
                  <span>
                    <strong>{story.title}</strong>
                    <small>{story.slug}</small>
                    <small className="mobile-story-status">{storyStatusLabel(story.status)}</small>
                  </span>
                </div>
              </td>
              <td><span className={`status status-${story.status.toLowerCase()}`}>{storyStatusLabel(story.status)}</span></td>
              <td><span className="structure-counts">{story.characterCount} 人物（{story.playableCharacterCount} 可扮演） · {story.sceneCount} 场景 · {story.endingCount} 结局</span></td>
              <td>R{story.draftRevision}</td>
              <td>{formatUpdatedAt(story.updatedAt)}</td>
              <td><button className="text-button" type="button" onClick={() => onOpen(story.id)}>打开</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StoryEditorDialog({
  storyId,
  onClose,
  onChanged,
}: {
  storyId: string;
  onClose: () => void;
  onChanged: () => void;
}): React.ReactNode {
  const [story, setStory] = useState<StoryDetail | null>(null);
  const [form, setForm] = useState({
    title: "",
    summary: "",
    worldSetting: "",
    immutableFacts: "",
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.storyDetail(storyId)
      .then((detail) => {
        setStory(detail);
        setForm({
          title: detail.title,
          summary: detail.summary,
          worldSetting: detail.worldSetting,
          immutableFacts: detail.immutableFacts,
        });
      })
      .catch((caught) => setError(caught instanceof ApiError ? caught.message : "无法读取故事线"))
      .finally(() => setLoading(false));
  }, [storyId]);

  async function save(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!story) return;
    setSubmitting(true);
    setError("");
    try {
      const updated = await api.updateStory(story.id, {
        expectedRevision: story.draftRevision,
        ...form,
      });
      setStory(updated);
      onChanged();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "保存故事线失败");
    } finally {
      setSubmitting(false);
    }
  }

  async function archive(): Promise<void> {
    if (!story) return;
    setSubmitting(true);
    setError("");
    try {
      await api.archiveStory(story.id, story.draftRevision);
      onChanged();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "归档故事线失败");
      setConfirmArchive(false);
    } finally {
      setSubmitting(false);
    }
  }

  const archived = story?.status === "ARCHIVED";

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="dialog editor-dialog" role="dialog" aria-modal="true" aria-labelledby="edit-story-title" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <div>
            <p className="eyebrow">STORY BASICS</p>
            <h2 id="edit-story-title">{story?.title ?? "故事线基本信息"}</h2>
          </div>
          <button className="icon-button" type="button" aria-label="关闭" onClick={onClose}>×</button>
        </header>
        {loading ? <div className="dialog-loading">正在读取故事线…</div> : null}
        {!loading && error && !story ? <div className="dialog-body"><ErrorNotice message={error} /></div> : null}
        {!loading && story ? (
          <form onSubmit={save}>
            <div className="editor-meta">
              <span className={`status status-${story.status.toLowerCase()}`}>{storyStatusLabel(story.status)}</span>
              <span>{story.slug}</span>
              <span>R{story.draftRevision}</span>
            </div>
            {error ? <ErrorNotice message={error} /> : null}
            {archived ? <div className="archive-note">该故事线已归档，当前仅供查看。</div> : null}
            <div className="editor-grid">
              <label>
                <span>故事线名称</span>
                <input value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} maxLength={120} disabled={archived} required />
              </label>
              <label className="wide-field">
                <span>一句话简介</span>
                <textarea value={form.summary} onChange={(event) => setForm({ ...form, summary: event.target.value })} maxLength={500} rows={3} disabled={archived} />
              </label>
              <label className="wide-field">
                <span>世界设定</span>
                <textarea value={form.worldSetting} onChange={(event) => setForm({ ...form, worldSetting: event.target.value })} maxLength={10000} rows={5} placeholder="时间、地点、背景和故事边界" disabled={archived} />
              </label>
              <label className="wide-field">
                <span>不可改写事实</span>
                <textarea value={form.immutableFacts} onChange={(event) => setForm({ ...form, immutableFacts: event.target.value })} maxLength={10000} rows={5} placeholder="AI 导演和角色都不能推翻的事实，每行一条" disabled={archived} />
              </label>
            </div>
            {confirmArchive ? (
              <div className="archive-confirm" role="alert">
                <div><strong>确认下线并归档这条故事线？</strong><span>归档后将从 APP 下线、从后台默认列表移出并停止编辑；正式版本、测试和玩家记录都会保留。</span></div>
                <button type="button" className="danger-button" disabled={submitting} onClick={() => void archive()}>{submitting ? "正在归档…" : "确认归档"}</button>
                <button type="button" className="text-button" onClick={() => setConfirmArchive(false)}>取消</button>
              </div>
            ) : null}
            <footer>
              {!archived && !confirmArchive ? <button className="danger-text-button" type="button" onClick={() => setConfirmArchive(true)}>归档故事线</button> : <span />}
              <div>
                <button className="secondary-button" type="button" onClick={onClose}>关闭</button>
                {!archived ? <button className="primary-button compact" disabled={submitting}>{submitting ? "正在保存…" : "保存基本信息"}</button> : null}
              </div>
            </footer>
          </form>
        ) : null}
      </section>
    </div>
  );
}

const AUDIT_ACTION_LABELS: Record<string, string> = {
  ADMIN_BOOTSTRAPPED: "初始化管理员",
  ADMIN_LOGIN: "管理员登录",
  ADMIN_LOGOUT: "管理员退出",
  ADMIN_PASSWORD_CHANGED: "修改密码",
  ADMIN_SESSION_REVOKED: "撤销会话",
  STORY_CREATED: "创建故事线",
  STORY_UPDATED: "更新故事线",
  STORY_ARCHIVED: "归档故事线",
  DIRECTOR_CONTROL_UPDATED: "更新真实 AI 设置",
  DIRECTOR_PROVIDER_CONFIG_CREATED: "创建 AI 模型设置",
  DOUBAO_CHARACTER_CONFIG_SAVED: "保存豆包角色扮演配置",
  DOUBAO_CHARACTER_CONFIG_DELETED: "删除豆包角色扮演配置",
  DOUBAO_CHARACTER_TEST_STARTED: "开始豆包连接检查",
  DOUBAO_CHARACTER_CONNECTION_TESTED: "完成豆包连接检查",
  DIRECTOR_PROVIDER_CONFIG_UPDATED: "更新 AI 模型设置",
  DIRECTOR_PROVIDER_CONFIG_DELETED: "删除 AI 模型设置",
  DIRECTOR_PROVIDER_CONNECTION_TESTED: "测试大模型连接",
  DOUBAO_TTS_PROVIDER_CONFIG_CREATED: "创建语音模型配置",
  DOUBAO_TTS_PROVIDER_CONFIG_UPDATED: "更新语音模型配置",
  DOUBAO_TTS_PROVIDER_CONFIG_DELETED: "删除语音模型配置",
  DOUBAO_TTS_GENERATION_CREATED: "提交语音生成任务",
  DOUBAO_TTS_GENERATION_SUCCEEDED: "语音生成完成",
  DOUBAO_TTS_GENERATION_FAILED: "语音生成失败",
  SEEDREAM_IMAGE_GENERATION_CREATED: "提交图片生成任务",
  SEEDREAM_IMAGE_GENERATION_SUCCEEDED: "图片生成完成",
  SEEDREAM_IMAGE_GENERATION_FAILED: "图片生成失败",
  OMNIHUMAN_GENERATION_CREATED: "提交人物视频任务",
  OMNIHUMAN_GENERATION_QUEUED: "人物视频进入队列",
  OMNIHUMAN_GENERATION_SUCCEEDED: "人物视频生成完成",
  OMNIHUMAN_GENERATION_FAILED: "人物视频生成失败",
  OMNIHUMAN_GENERATION_EXPIRED: "人物视频任务过期",
  SEEDANCE_PROVIDER_CONFIG_CREATED: "创建视频模型配置",
  SEEDANCE_PROVIDER_CONFIG_UPDATED: "更新视频模型配置",
  SEEDANCE_PROVIDER_CONFIG_DELETED: "删除视频模型配置",
  SEEDANCE_VIDEO_GENERATION_CREATED: "提交视频生成任务",
  SEEDANCE_VIDEO_GENERATION_FAILED: "视频任务提交失败",
  SEEDANCE_VIDEO_GENERATION_SUCCEEDED: "视频生成完成",
  SEEDANCE_VIDEO_GENERATION_ENDED: "视频生成结束",
  WEBSITE_WAITLIST_UPDATED: "更新内测申请",
  WEBSITE_WAITLIST_REAPPLIED: "申请人重新申请",
};

function AuditLogPage(): React.ReactNode {
  const [logs, setLogs] = useState<AuditLogItem[]>([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationView>({ page: 1, pageSize: 30, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");
    api.auditLogs(page)
      .then((result) => {
        setLogs(result.data);
        setPagination(result.pagination);
      })
      .catch((caught) => setError(caught instanceof ApiError ? caught.message : "无法读取审计日志"))
      .finally(() => setLoading(false));
  }, [page]);

  return (
    <>
      <div className="page-heading">
        <div>
          <p className="eyebrow">AUDIT TRAIL</p>
          <h1>审计日志</h1>
          <p>追踪登录、改密、会话撤销和故事线敏感操作。</p>
        </div>
      </div>
      {error ? <div className="state-card"><ErrorNotice message={error} /></div> : null}
      {!error && loading ? <div className="table-skeleton"><span /><span /><span /></div> : null}
      {!error && !loading ? (
        <div className="table-shell audit-table">
          <table>
            <thead><tr><th>时间</th><th>操作</th><th>操作者</th><th>对象</th><th>来源 IP</th><th>请求 ID</th></tr></thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id}>
                  <td>{formatUpdatedAt(log.createdAt)}</td>
                  <td><strong>{AUDIT_ACTION_LABELS[log.action] ?? log.action}</strong></td>
                  <td>{log.actor?.displayName ?? "系统"}<small>{log.actor?.username ?? "system"}</small></td>
                  <td>{log.targetType}<small>{log.targetId?.slice(0, 12) ?? "—"}</small></td>
                  <td>{log.ip ?? "—"}</td>
                  <td><code>{log.requestId?.slice(0, 18) ?? "—"}</code></td>
                </tr>
              ))}
              {!logs.length ? <tr><td colSpan={6} className="empty-table-cell">暂无审计记录</td></tr> : null}
            </tbody>
          </table>
        </div>
      ) : null}
      <PaginationControls pagination={pagination} onPage={setPage} />
    </>
  );
}

function sessionDevice(userAgent: string | null): string {
  if (!userAgent) return "未知设备";
  if (userAgent.includes("Macintosh")) return "Mac 浏览器";
  if (userAgent.includes("iPhone") || userAgent.includes("iPad")) return "iOS 浏览器";
  if (userAgent.includes("Android")) return "Android 浏览器";
  if (userAgent.includes("Windows")) return "Windows 浏览器";
  return "浏览器会话";
}

function SessionsPage(): React.ReactNode {
  const [sessions, setSessions] = useState<AdminSessionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revoking, setRevoking] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setSessions(await api.sessions());
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "无法读取登录会话");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function revoke(id: string): Promise<void> {
    setRevoking(id);
    setError("");
    try {
      await api.revokeSession(id);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "撤销会话失败");
    } finally {
      setRevoking("");
    }
  }

  return (
    <>
      <div className="page-heading">
        <div>
          <p className="eyebrow">ACCOUNT SECURITY</p>
          <h1>登录会话</h1>
          <p>查看当前账号最近的登录设备，并撤销不再使用的会话。</p>
        </div>
      </div>
      {error ? <ErrorNotice message={error} /> : null}
      {loading ? <div className="table-skeleton"><span /><span /><span /></div> : null}
      {!loading ? (
        <div className="session-list">
          {sessions.map((session) => {
            const active = !session.revokedAt && new Date(session.expiresAt).getTime() > Date.now();
            return (
              <article className="session-item" key={session.id}>
                <span className="session-icon" aria-hidden="true">▣</span>
                <div><strong>{sessionDevice(session.userAgent)} {session.current ? <em>当前会话</em> : null}</strong><span>{session.ip ?? "未知 IP"} · 登录于 {formatUpdatedAt(session.createdAt)}</span></div>
                <span className={`session-state ${active ? "active" : "ended"}`}>{active ? "有效" : "已结束"}</span>
                {!session.current && active ? <button className="danger-text-button" disabled={revoking === session.id} onClick={() => void revoke(session.id)}>{revoking === session.id ? "正在撤销…" : "撤销"}</button> : null}
              </article>
            );
          })}
          {!sessions.length ? <div className="empty-state"><h2>没有会话记录</h2></div> : null}
        </div>
      ) : null}
    </>
  );
}

function PaginationControls({
  pagination,
  onPage,
}: {
  pagination: PaginationView;
  onPage: (page: number) => void;
}): React.ReactNode {
  if (pagination.totalPages <= 1) return null;
  return (
    <div className="pagination">
      <span>第 {pagination.page} / {pagination.totalPages} 页，共 {pagination.total} 条</span>
      <div>
        <button className="secondary-button" disabled={pagination.page <= 1} onClick={() => onPage(pagination.page - 1)}>上一页</button>
        <button className="secondary-button" disabled={pagination.page >= pagination.totalPages} onClick={() => onPage(pagination.page + 1)}>下一页</button>
      </div>
    </div>
  );
}

function Dashboard({ session, onLogout }: { session: AdminSessionView; onLogout: () => void }): React.ReactNode {
  const [view, setView] = useState<"stories" | "image" | "voice" | "character-video" | "playtests" | "simulations" | "versions" | "waitlist" | "provider" | "director" | "video" | "audit" | "sessions" | "responsibilities">("stories");
  const [stories, setStories] = useState<StoryListItem[]>([]);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<StoryStatus | "">("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationView>({ page: 1, pageSize: 20, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editingStoryId, setEditingStoryId] = useState("");
  const [playtestStoryId, setPlaytestStoryId] = useState("");
  const [simulationStoryId, setSimulationStoryId] = useState("");
  const [simulationRunId, setSimulationRunId] = useState("");
  const [versionStoryId, setVersionStoryId] = useState("");

  const loadStories = useCallback(async () => {
    if (view !== "stories") return;
    setLoading(true);
    setError("");
    try {
      const result = await api.storyLines({ q: query, status, page, pageSize: 20 });
      setStories(result.data);
      setPagination(result.pagination);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "无法读取故事线");
    } finally {
      setLoading(false);
    }
  }, [page, query, status, view]);

  useEffect(() => {
    const timer = window.setTimeout(() => void loadStories(), 180);
    return () => window.clearTimeout(timer);
  }, [loadStories]);

  const viewLabel = view === "stories" ? (editingStoryId ? "结构编辑器" : "故事线") : view === "image" ? "图片生成" : view === "voice" ? "语音生成" : view === "character-video" ? "人物视频" : view === "playtests" ? "内部试玩" : view === "simulations" ? "规则测试" : view === "versions" ? "版本与验证" : view === "waitlist" ? "内测申请" : view === "provider" ? "AI 模型设置" : view === "director" ? "真实 AI 管理" : view === "video" ? "视频生成" : view === "audit" ? "审计日志" : view === "responsibilities" ? "职责说明" : "登录会话";
  const viewGroup = view === "stories" || view === "image" || view === "voice" || view === "character-video" || view === "video" || view === "playtests" || view === "simulations" ? "内容制作" : view === "versions" ? "发布管理" : view === "waitlist" ? "官网运营" : view === "provider" || view === "director" ? "模型运营" : "系统";

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <Brand />
        <nav aria-label="主导航">
          <p>内容制作</p>
          <button className={`nav-link ${view === "stories" ? "active" : ""}`} onClick={() => { setView("stories"); setEditingStoryId(""); }}><span aria-hidden="true">◇</span>故事线</button>
          {session.role === "SUPER_ADMIN" ? <button className={`nav-link ${view === "video" ? "active" : ""}`} onClick={() => setView("video")}><span aria-hidden="true">▸</span>视频生成<small>Seedance</small></button> : null}
          <button className={`nav-link ${view === "playtests" ? "active" : ""}`} onClick={() => { setPlaytestStoryId(""); setView("playtests"); }}><span aria-hidden="true">▶</span>内部试玩<small>体验</small></button>
          <button className={`nav-link ${view === "simulations" ? "active" : ""}`} onClick={() => { setSimulationStoryId(""); setSimulationRunId(""); setView("simulations"); }}><span aria-hidden="true">▷</span>规则测试<small>规则</small></button>
          <p>发布管理</p>
          <button className={`nav-link ${view === "versions" ? "active" : ""}`} onClick={() => { setVersionStoryId(""); setView("versions"); }}><span aria-hidden="true">◎</span>版本与验证</button>
          {session.role === "SUPER_ADMIN" ? <><p>官网运营</p><button className={`nav-link ${view === "waitlist" ? "active" : ""}`} onClick={() => setView("waitlist")}><span aria-hidden="true">✦</span>内测申请<small>名单</small></button></> : null}
          {session.role === "SUPER_ADMIN" ? <><p>模型运营</p><button className={`nav-link ${view === "provider" ? "active" : ""}`} onClick={() => setView("provider")}><span aria-hidden="true">⌁</span>AI 模型设置<small>模型</small></button><button className={`nav-link ${view === "director" ? "active" : ""}`} onClick={() => setView("director")}><span aria-hidden="true">◉</span>真实 AI 管理<small>使用</small></button></> : null}
          <p>系统</p>
          <button className={`nav-link ${view === "responsibilities" ? "active" : ""}`} aria-current={view === "responsibilities" ? "page" : undefined} title="职责说明" onClick={() => setView("responsibilities")}><span aria-hidden="true">▤</span>职责说明</button>
          {session.role === "SUPER_ADMIN" ? <button className={`nav-link ${view === "audit" ? "active" : ""}`} onClick={() => setView("audit")}><span aria-hidden="true">≡</span>审计日志</button> : null}
          <button className={`nav-link ${view === "sessions" ? "active" : ""}`} onClick={() => setView("sessions")}><span aria-hidden="true">▣</span>登录会话</button>
        </nav>
      </aside>
      <main className="workspace">
        <header className="topbar">
          <div><span className="crumb">{viewGroup}</span><span>/</span><strong>{viewLabel}</strong></div>
          <div className="operator">
            <span className="avatar">{session.displayName.slice(0, 1)}</span>
            <span><strong>{session.displayName}</strong><small>{session.role === "SUPER_ADMIN" ? "超级管理员" : "编辑"}</small></span>
            <button className="text-button" onClick={onLogout}>退出</button>
          </div>
        </header>
        <section className={`content ${view === "simulations" ? "simulation-content" : ""} ${view === "playtests" ? "playtest-content" : ""} ${view === "versions" ? "release-content" : ""}`}>
          {view === "stories" && !editingStoryId ? (
            <>
              <div className="page-heading">
                <div>
                  <p className="eyebrow">STORY LINE LIBRARY</p>
                  <h1>故事线</h1>
                  <p>管理互动故事的角色、场景、状态和合法结局。</p>
                </div>
                <button className="primary-button compact" onClick={() => setShowCreate(true)}>＋ 创建故事线</button>
              </div>
              <div className="toolbar">
                <label className="search-box">
                  <span aria-hidden="true">⌕</span>
                  <span className="sr-only">搜索故事线</span>
                  <input value={query} onChange={(event) => { setQuery(event.target.value); setPage(1); }} placeholder="搜索名称或英文标识" />
                </label>
                <label className="status-filter">
                  <span className="sr-only">筛选状态</span>
                  <select value={status} onChange={(event) => { setStatus(event.target.value as StoryStatus | ""); setPage(1); }}>
                    <option value="">全部状态</option>
                    <option value="DRAFT">草稿</option>
                    <option value="READY">待发布</option>
                    <option value="PUBLISHED">已发布</option>
                    <option value="ARCHIVED">已归档</option>
                  </select>
                </label>
                <span className="result-count">{loading ? "正在读取…" : `${pagination.total} 条故事线`}</span>
              </div>
              {error ? <div className="state-card"><ErrorNotice message={error} /><button className="secondary-button" onClick={() => void loadStories()}>重新加载</button></div> : null}
              {!error && loading ? <div className="table-skeleton" aria-label="正在加载故事线"><span /><span /><span /></div> : null}
              {!error && !loading && stories.length ? <StoryTable stories={stories} onOpen={setEditingStoryId} /> : null}
              {!error && !loading && !stories.length ? (
                <div className="empty-state">
                  <span className="empty-glyph" aria-hidden="true">幕</span>
                  <h2>{query || status ? "没有匹配的故事线" : "从第一条故事线开始"}</h2>
                  <p>{query || status ? "尝试调整搜索词或状态。" : "先建立故事世界和角色，随后就能进入规则测试。"}</p>
                  {!query && !status ? <button className="primary-button compact" onClick={() => setShowCreate(true)}>创建故事线</button> : null}
                </div>
              ) : null}
              <PaginationControls pagination={pagination} onPage={setPage} />
            </>
          ) : null}
          {view === "stories" && editingStoryId ? <StoryStructureEditor storyId={editingStoryId} onClose={() => setEditingStoryId("")} onChanged={() => void loadStories()} onPlaytest={() => { setPlaytestStoryId(editingStoryId); setView("playtests"); }} onSimulate={() => { setSimulationStoryId(editingStoryId); setSimulationRunId(""); setView("simulations"); }} onPublish={() => { setVersionStoryId(editingStoryId); setView("versions"); }} /> : null}
          {view === "playtests" ? <PlaytestWorkbench initialStoryId={playtestStoryId} onReview={(runId) => { setSimulationRunId(runId); setSimulationStoryId(""); setView("simulations"); }} /> : null}
          {view === "simulations" ? <SimulationWorkbench initialStoryId={simulationStoryId} initialRunId={simulationRunId} /> : null}
          {view === "versions" ? <VersionPublishWorkbench initialStoryId={versionStoryId} canPublish={session.role === "SUPER_ADMIN"} onEditStory={(storyId) => { setEditingStoryId(storyId); setView("stories"); }} /> : null}
          {view === "waitlist" ? <WebsiteWaitlistPage /> : null}
          {view === "provider" ? <DirectorProviderConfigPage /> : null}
          {view === "director" ? <DirectorOperationsPage /> : null}
          {view === "image" ? <SeedreamImagePage onConfigureProvider={() => setView("provider")} /> : null}
          {view === "voice" ? <DoubaoTtsGenerationPage onConfigureProvider={() => setView("provider")} /> : null}
          {view === "character-video" ? <OmniHumanGenerationPage onConfigureProvider={() => setView("provider")} /> : null}
          {view === "video" ? <SeedanceVideoPage onConfigureProvider={() => setView("provider")} /> : null}
          {view === "audit" ? <AuditLogPage /> : null}
          {view === "sessions" ? <SessionsPage /> : null}
          {view === "responsibilities" ? <ResponsibilityGuidePage /> : null}
        </section>
      </main>
      {showCreate ? <CreateStoryDialog onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); void loadStories(); }} /> : null}
    </div>
  );
}

export function App(): React.ReactNode {
  const [session, setSession] = useState<AdminSessionView | null | undefined>(undefined);

  useEffect(() => {
    api.me().then(setSession).catch((caught) => {
      if (caught instanceof ApiError && caught.status === 401) setSession(null);
      else setSession(null);
    });
  }, []);

  if (session === undefined) {
    return <main className="boot-screen"><Brand /><span className="boot-line" /></main>;
  }

  if (!session) return <LoginView onLogin={setSession} />;
  if (session.mustChangePassword) {
    return <ChangePasswordView onChanged={() => setSession({ ...session, mustChangePassword: false })} />;
  }

  async function logout(): Promise<void> {
    try {
      await api.logout();
    } finally {
      setSession(null);
    }
  }

  return <Dashboard session={session} onLogout={() => void logout()} />;
}
